import { GearSixIcon, ListIcon, QuestionIcon } from '@phosphor-icons/react';
import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { KeyboardIcon, LoaderIcon } from 'lucide-react';
import { useState, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import mesaLogo from '@/assets/logo-bw-transparent.svg';
import { AIOutageBanner } from '@/components/ai-outage-banner';
import { KbdTooltip } from '@/components/kbd';
import { KeyboardShortcutsDialog } from '@/components/keyboard-shortcuts-dialog';
import { MousedownLink } from '@/components/mousedown-link';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { UserMenu } from '@/components/user-menu';
import { useGlobalShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { useZero } from '@/lib/zero';
import { useCurrentOrg } from '@/services/org';

export const Route = createFileRoute('/_authed/_layout')({
  component: () => (
    <ErrorBoundary fallback={<div className="p-4">Error loading layout</div>}>
      <Suspense fallback={<div className="size-full flex animate-pulse bg-stone-50" />}>
        <MainLayout />
      </Suspense>
    </ErrorBoundary>
  ),
});

const MainLayout = () => {
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const { currentOrg } = useCurrentOrg();
  const z = useZero();

  const [runningBackfill] = useZeroQuery(
    z.query.BackfillStatusTable.where(({ and, cmp }) =>
      and(cmp('installationId', currentOrg.id), cmp('status', 'running'))
    ).one()
  );

  useGlobalShortcuts({
    openShortcutsDialog: () => setIsShortcutsOpen(true),
  });

  return (
    <div className="size-full flex">
      {/* Left side navbar */}
      <div className="shrink-0 w-12 overflow-hidden flex flex-col h-full justify-between items-center border-r border-stone-300 bg-stone-200">
        <div className="flex flex-col items-center gap-3 py-2">
          <div className="flex flex-col items-center">
            <MousedownLink to="/pulls" preload="intent" className="p-2 rounded">
              <img src={mesaLogo} alt="Mesa Valley" className="size-6" />
            </MousedownLink>
            <Separator orientation="horizontal" className="h-full bg-stone-400 mt-[7px]" />
          </div>
          <MousedownLink
            to="/pulls"
            preload="intent"
            activeProps={{ className: 'bg-stone-300' }}
            className="p-2 rounded hover:bg-stone-300"
          >
            <KbdTooltip content="Pull Requests" shortcut={['g', 'p']} side="right" align="start">
              <ListIcon size={20} weight="fill" className="text-foreground" />
            </KbdTooltip>
          </MousedownLink>
          <MousedownLink
            to="/$owner/settings"
            params={{ owner: currentOrg.account.login }}
            preload="intent"
            activeProps={{ className: 'bg-stone-300' }}
            className="p-2 rounded hover:bg-stone-300"
          >
            <KbdTooltip content="Settings" shortcut={['g', 's']} side="right" align="start">
              <GearSixIcon size={20} weight="fill" className="text-foreground" />
            </KbdTooltip>
          </MousedownLink>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 pb-2">
          {runningBackfill && (
            <div className="flex items-center gap-2 rounded-md bg-sky-200 p-2">
              <Tooltip>
                <TooltipTrigger>
                  <LoaderIcon className="size-4 animate-spin text-sky-800" />
                </TooltipTrigger>
                <TooltipContent>
                  Backfilling data{' '}
                  {runningBackfill.steps!.find((step) => step.status === 'running')?.progress ? (
                    <>
                      ({runningBackfill.steps!.find((step) => step.status === 'running')!.progress!.current} of{' '}
                      {runningBackfill.steps!.find((step) => step.status === 'running')!.progress!.total})
                    </>
                  ) : (
                    ''
                  )}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
          <Button
            variant="ghost"
            title="Keyboard Shortcuts"
            className="size-10 hover:bg-stone-300 rounded-md"
            onClick={() => setIsShortcutsOpen(true)}
          >
            <KeyboardIcon className="size-5" strokeWidth={1.25} />
          </Button>
          <KeyboardShortcutsDialog open={isShortcutsOpen} onOpenChange={setIsShortcutsOpen} />

          <Button
            id="mesa-support-button"
            variant="ghost"
            title="Support"
            className="size-10 hover:bg-stone-300 rounded-md"
          >
            <QuestionIcon className="size-6" weight="fill" />
          </Button>

          <UserMenu />
        </div>
      </div>
      {/* Main */}
      <div className="flex-1 size-full bg-stone-50 overflow-hidden flex flex-col">
        <AIOutageBanner />
        <div className="flex-1 overflow-hidden">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
